import { use, useState, type ReactNode, type Ref } from "react";
import { describeError } from "@/api/client";
import { Button, NumberField, Tooltip } from "@/components";
import type { DecimalString } from "@/lib/decimal";
import { WeightCaptureContext } from "../api";
import styles from "./WeightInput.module.css";

export interface WeightInputProps {
  label: ReactNode;
  value: DecimalString | null;
  onValueChange: (value: DecimalString | null) => void;
  name?: string;
  description?: ReactNode;
  error?: string | null;
  optional?: boolean;
  size?: "md" | "lg";
  inputRef?: Ref<HTMLInputElement>;
  disabled?: boolean;
}

/**
 * A mass in grams at the balance resolution (0.001 g). Typed by hand or by a keyboard-wedge balance;
 * when a WeightCaptureContext is provided (the balance driver), a "Read balance" button fills it.
 */
export function WeightInput({
  label,
  value,
  onValueChange,
  name,
  description,
  error,
  optional,
  size = "md",
  inputRef,
  disabled,
}: WeightInputProps) {
  const balance = use(WeightCaptureContext);
  const [reading, setReading] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);

  async function capture() {
    if (!balance) return;
    setReading(true);
    setCaptureError(null);
    try {
      onValueChange(await balance.capture());
    } catch (failure) {
      setCaptureError(describeError(failure));
    } finally {
      setReading(false);
    }
  }

  return (
    <div className={styles.weight}>
      <NumberField
        label={label}
        name={name}
        value={value}
        onValueChange={onValueChange}
        unit="g"
        min={0}
        step={0.001}
        size={size}
        description={description}
        error={error ?? captureError}
        optional={optional}
        inputRef={inputRef}
        disabled={disabled}
        className={styles.field}
      />
      {balance ? (
        <Tooltip content={balance.ready ? balance.status : (balance.status ?? "The balance isn't connected")}>
          <Button
            icon="scale"
            size={size}
            className={styles.capture}
            onClick={() => void capture()}
            pending={reading}
            pendingLabel="Reading…"
            disabled={!balance.ready || disabled}
            focusableWhenDisabled
          >
            {balance.label}
          </Button>
        </Tooltip>
      ) : null}
    </div>
  );
}
